import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {ADD_ICON_WITH_HOVER_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import FilterEditorModal from 'data-frames/filters/components/FilterEditorModal';
import getConfig from 'getConfig';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import InputAwareStepConfigEditorModal from 'pipelines/generalized-step-config-editor/InputAwareStepConfigEditorModal';
import {isStepInputParentsComplete} from 'pipelines/generalized-step-config-editor/StepInputParentsConfiguration';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {AddOrReplaceAndRunStepParams, DraftStepInputParents} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import Tooltip from 'ui-library/bs/Tooltip';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import AddIcon from 'ui-library/icons/AddIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {ADDABLE_TRANSFORMATION_STEPS, GATED_TRANSFORMATION_STEPS} from './VisualizationStepEditorConstants';

export const FIX_FAILED_TRANSFORMS_MESSAGE =
  'Fix all failed transformations before adding more transformations';
export const WAITING_FOR_STEP_SAVE_MESSAGE = 'Waiting for step to save';
export const BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE =
  'Cannot add transformations when there is a manual flagging downstream';

interface AddTransformStepDropdownProps {
  viewSteps: Array<GetAnalysisPipelineStep>;
  dataFrameSpec: DataFrameSpec;
  inputDataFrameId: string;
  analysis: AnalysisGraphQL;
  canAddNewSteps: boolean;
  addNewStepsDisabledMessage?: string;
}

export default function AddTransformStepDropdown({
  viewSteps,
  dataFrameSpec,
  inputDataFrameId,
  analysis,
  canAddNewSteps,
  addNewStepsDisabledMessage = FIX_FAILED_TRANSFORMS_MESSAGE,
}: AddTransformStepDropdownProps): JSX.Element {
  const endsInTerminalViewStep = isViewTerminalStepType(viewSteps[viewSteps.length - 1].definition.stepType);
  const lastNonTerminalStep = viewSteps.findLast((step) => !isViewTerminalStepType(step.definition.stepType));

  assert(lastNonTerminalStep != null, 'Expected viewSteps to contain a non-terminal step');
  const lastNonTerminalStepShape =
    PIPELINE_STEP_SHAPES[lastNonTerminalStep.definition.stepType][lastNonTerminalStep.definition.stepVersion];
  assert(
    lastNonTerminalStepShape.outputDataShapes.hasOwnProperty(SINGLE_DATASET_OUTPUT_STEP_DATA_NAME),
    `Step type ${lastNonTerminalStep.definition.stepType} does not have SINGLE_DATASET_OUTPUT_STEP_DATA_NAME output data to use as input for new transform steps`
  );

  const stepOutputChildren = endsInTerminalViewStep
    ? {
        [SINGLE_DATASET_OUTPUT_STEP_DATA_NAME]: [
          {
            stepId: viewSteps[viewSteps.length - 1].id,
            stepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
          },
        ],
      }
    : undefined;

  const allAddableTransformSteps = getConfig('ENABLE_ANALYSIS_TOOL_IN_DEVELOPMENT_TRANSFORMS')
    ? [...ADDABLE_TRANSFORMATION_STEPS, ...GATED_TRANSFORMATION_STEPS]
    : ADDABLE_TRANSFORMATION_STEPS;

  const sectionedOptions = [];

  for (const section of allAddableTransformSteps) {
    const options = section.map(({stepType, stepVersion}) => ({
      key: stepType,
      text: STEP_TYPE_TO_USER_FACING_NAME[stepType],
      onSelect: () => {
        runModal(InputAwareStepConfigEditorModal, {
          stepType,
          stepVersion,
          inputDataFrameSpec: dataFrameSpec,
          analysisId: analysis.id,
          precedingStep: lastNonTerminalStep,
          step: null,
          handleRequestSubmit: (
            config: PipelineStepConfig<PipelineStepType, number>,
            stepInputParents: DraftStepInputParents | null
          ) => {
            assert(
              stepInputParents == null || isStepInputParentsComplete(stepInputParents, stepType, stepVersion),
              'Expected step input parents information to be complete if defined'
            );
            return withLoadingScreen(
              addOrReplaceAndRunAnalysisStep(analysis.id, {
                stepType,
                stepVersion,
                stepConfig: config,
                stepInput: {inputDataFrameId},
                stepOutputChildren,
                ...(stepInputParents == null ? {} : {stepInputParents}),
              } as unknown as AddOrReplaceAndRunStepParams),
              'Updating transformations...'
            );
          },
        });
      },
    }));
    options.sort((a, b) => a.text.localeCompare(b.text));
    sectionedOptions.push(options);
  }

  return (
    <Tooltip disabled={canAddNewSteps} title={addNewStepsDisabledMessage}>
      <Dropdown
        menu={
          <Menu dropLeft={true} width={MenuWidth.MEDIUM} height={MenuHeight.MEDIUM}>
            <Section>
              <Option
                onSelect={() => {
                  runModal(FilterEditorModal, {
                    analysis,
                    viewSteps,
                    canSelectColumn: true,
                    canAddNewFilters: canAddNewSteps,
                  });
                }}
              >
                Filter dataset
              </Option>
            </Section>
            {sectionedOptions.map((options, i) => (
              <Section key={i}>
                {options.map(({key, onSelect, text}) => (
                  <Option
                    key={key}
                    onSelect={onSelect}
                    dataTestAttrs={{component: 'AddTransformStepDropdown', element: 'option', key}}
                  >
                    {text}
                  </Option>
                ))}
              </Section>
            ))}
          </Menu>
        }
      >
        <AddIcon
          disabled={!canAddNewSteps}
          className={css(styles.addIcon)}
          dataTestAttrs={{component: 'AddTransformStepDropdown', element: 'addButton'}}
        />
      </Dropdown>
    </Tooltip>
  );
}

/** @package */
export const styles = StyleSheet.create({
  addIcon: ADD_ICON_WITH_HOVER_STYLES,
});
